import { useEffect, useMemo, useState } from 'react';
import { onValue, ref, query, orderByChild, limitToLast } from 'firebase/database';
import { rtdb } from '../firebase';
import { useEstablishmentAuth } from '../context/EstablishmentAuthContext';

/**
 * Live feed of meal reviews for CROUS managed by the current admin.
 *
 * RTDB path: reviews/{autoId}
 *   {
 *     crousId: string,
 *     rating: 1..5,
 *     comment?: string,
 *     tags?: string[],       // e.g. ["qualité", "attente", "quantité"]
 *     mealType?: string,     // "midi" | "soir"
 *     timestamp: number,
 *     deviceId?: string
 *   }
 */

const LIMIT = 500;

export default function useReviews() {
  const { adminDoc, status } = useEstablishmentAuth();
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (status !== 'authorized') {
      setRaw([]);
      setLoading(false);
      return undefined;
    }
    const q = query(ref(rtdb, 'reviews'), orderByChild('timestamp'), limitToLast(LIMIT));
    return onValue(
      q,
      (snap) => {
        const rows = [];
        snap.forEach((c) => {
          const v = c.val();
          if (v && v.crousId && typeof v.rating === 'number' && typeof v.timestamp === 'number') {
            rows.push({ id: c.key, ...v });
          }
        });
        rows.sort((a, b) => b.timestamp - a.timestamp);
        setRaw(rows);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [status]);

  const reviews = useMemo(() => {
    if (!adminDoc) return [];
    if (adminDoc.role === 'super') return raw;
    const allowed = adminDoc.crousIds || {};
    return raw.filter((r) => allowed[r.crousId] === true);
  }, [raw, adminDoc]);

  const stats = useMemo(() => {
    if (reviews.length === 0) return { avg: 0, count: 0, distribution: [0, 0, 0, 0, 0], byCrous: {} };
    const dist = [0, 0, 0, 0, 0];
    const byCrous = {};
    let total = 0;
    for (const r of reviews) {
      total += r.rating;
      dist[r.rating - 1] += 1;
      if (!byCrous[r.crousId]) byCrous[r.crousId] = { total: 0, count: 0, sum: 0 };
      byCrous[r.crousId].count += 1;
      byCrous[r.crousId].sum += r.rating;
    }
    for (const k of Object.keys(byCrous)) {
      byCrous[k].avg = byCrous[k].sum / byCrous[k].count;
    }
    return {
      avg: total / reviews.length,
      count: reviews.length,
      distribution: dist,
      byCrous
    };
  }, [reviews]);

  const tagStats = useMemo(() => {
    const map = {};
    for (const r of reviews) {
      if (!r.tags) continue;
      for (const t of r.tags) {
        if (!map[t]) map[t] = 0;
        map[t] += 1;
      }
    }
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [reviews]);

  return { reviews, stats, tagStats, loading };
}
